import type { Job, Contractor, Issue, CustomerCredit, ContractorOpportunity } from "./types";
import type { BuildReviewEvent } from "./buildReviewTypes";
import type { MaterialKit } from "./materialKit";
import { getKit } from "./materialKit";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import { getContractorReliability, type ReliabilityTier } from "./contractorReliability";
import { getInstallReadiness } from "@/components/JobWorkflowStatus";
import { deriveReadinessFromJob } from "./readiness";

export type RiskFlag = 
  | "High Risk Assignment"
  | "Low Experience for Job Type"
  | "High Issue Rate"
  | "High Credit Impact"
  | "Low Acceptance Rate"
  | "Contractor Unavailable";

export type DispatchDecision = "recommended" | "overridden";

export interface DispatchOverride {
  recommendedContractorId: string;
  selectedContractorId: string;
  decision: DispatchDecision;
  reason?: string;
  timestamp: string;
  decidedBy: string;
}

export interface ContractorRecommendation {
  contractor: Contractor;
  score: number; // 0-100 composite dispatch score
  reliabilityTier: ReliabilityTier;
  reliabilityScore: number;
  riskFlags: RiskFlag[];
  tradeMatch: boolean;
  regionMatch: boolean;
  availabilityOk: boolean;
  jobTypeExperience: number; // count of similar jobs
  rank: number;
}

/**
 * Categorical readiness gap surfaced to the assign UI. Each gap maps to a
 * push-mode recovery chip (resolve issue / order materials / open permit / kit).
 * Surfacing a gap NEVER blocks assignment — dispatch is gated separately.
 */
export type ReadinessGapKind = "permit" | "materials" | "kit" | "issue";
export interface ReadinessGap {
  kind: ReadinessGapKind;
  label: string;
  detail?: string;
}

export interface DispatchRecommendationResult {
  jobReady: boolean;
  readinessStatus: string;
  recommendations: ContractorRecommendation[];
  topPick: ContractorRecommendation | null;
  /** True only for terminal states (e.g. job already completed). Never set for readiness gaps. */
  blocked: boolean;
  blockReason?: string;
  /** True when assignment is allowed but dispatch (start-of-work) is gated by readiness. */
  gated: boolean;
  /** Operator-facing summary of why dispatch is gated, paired with recovery chips. */
  gateReason?: string;
  /** Categorical recovery hints — drive Push-Mode chips in QuickAssignModal. */
  gateBlockers: ReadinessGap[];
}

export function getDispatchRecommendations(
  job: Job,
  contractors: Contractor[],
  opportunities: ContractorOpportunity[],
  jobs: Job[],
  issues: Issue[],
  credits: CustomerCredit[],
  buildReviewEvents: BuildReviewEvent[] = [],
  materialKits: MaterialKit[] = [],
): DispatchRecommendationResult {
  const jobIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));
  const readiness = getInstallReadiness(job, jobIssues, buildReviewEvents);
  const jobReady = readiness.status !== "Not Ready";
  const canonical = deriveReadinessFromJob(job, buildReviewEvents, getKit(materialKits, job.id));

  // Build categorical gaps for Push-Mode recovery chips. Gaps DO NOT block assignment —
  // dispatch (start-of-work) is gated, but contractor selection always remains available.
  const gateBlockers: ReadinessGap[] = [];
  if (job.permitStatus && job.permitStatus !== "Approved" && job.permitStatus !== "Not Required") {
    gateBlockers.push({ kind: "permit", label: "Open permit", detail: `Permit ${job.permitStatus}` });
  }
  if (canonical.materials.status !== "ready") {
    const materialState = canonical.materials.state;
    const materialText = `${canonical.materials.shortText ?? ""} ${canonical.materials.description ?? ""}`.toLowerCase();
    // "ordered" = PO placed, materials in transit — job is safe to schedule;
    // do not create a gate blocker so it doesn't appear as "at risk" on the
    // dispatch rail or the Ready-to-Book dialog.
    if (materialState !== "ordered") {
      const isKitBlock = materialText.includes("kit not generated") || materialText.includes("kit not cleared");
      if (isKitBlock) {
        gateBlockers.push({
          kind: "kit",
          label: materialText.includes("kit not generated") ? "Generate material kit" : "Confirm material kit",
          detail: canonical.materials.shortText || `Materials ${job.materialReadiness}`,
        });
      } else {
        const materialLabel =
          materialState === "not_ordered"
            ? "Order materials"
            : materialState === "partially_received"
              ? "Awaiting material receiving"
              : "Resolve materials";
        gateBlockers.push({
          kind: "materials",
          label: materialLabel,
          detail: canonical.materials.shortText || `Materials ${job.materialReadiness}`,
        });
      }
    }
  }
  if (jobIssues.length > 0) {
    const urgent = jobIssues.find(i => i.severity === "Urgent");
    gateBlockers.push({ kind: "issue", label: "Resolve issue", detail: urgent ? `${jobIssues.length} open · 1 urgent` : `${jobIssues.length} open` });
  }
  const gated = !jobReady && gateBlockers.length > 0;
  const gateReason = gated
    ? `Assignment is allowed. Dispatch unlocks once ${gateBlockers[0].label.toLowerCase()} is cleared.`
    : undefined;

  if (job.currentStage === "Completed") {
    return {
      jobReady: true, readinessStatus: "Completed", recommendations: [], topPick: null,
      blocked: true, blockReason: "Job already completed",
      gated: false, gateBlockers: [],
    };
  }

  // Filter eligible contractors
  const eligible = contractors.filter(c => {
    if (!c.active) return false;
    if (c.onboardingStatus !== "Approved") return false;
    return true;
  });

  const recommendations: ContractorRecommendation[] = eligible.map(contractor => {
    const scorecard = computeScorecard(contractor, opportunities, jobs, issues, credits);
    const reliability = getContractorReliability(scorecard);
    const riskFlags: RiskFlag[] = [];

    // Trade match
    const tradeMatch = contractor.tradeType.toLowerCase().includes("bath") ||
      contractor.tradeType.toLowerCase().includes("general") ||
      contractor.tradeType.toLowerCase().includes(job.projectType.toLowerCase().split(" ")[0]);

    // Region match
    const jobCity = job.address.city.toLowerCase();
    const regionMatch = contractor.serviceRegions.some(r => 
      r.toLowerCase().includes(jobCity) || jobCity.includes(r.toLowerCase())
    ) || contractor.serviceArea.toLowerCase().includes(jobCity);

    // Availability
    const availabilityOk = contractor.availabilityStatus !== "Unavailable";
    if (!availabilityOk) riskFlags.push("Contractor Unavailable");

    // Job type experience
    const similarJobs = jobs.filter(j => 
      j.assignedSubcontractor === contractor.companyName &&
      j.projectType === job.projectType
    );
    const jobTypeExperience = similarJobs.length;
    if (jobTypeExperience === 0 && scorecard.assignedJobCount > 0) {
      riskFlags.push("Low Experience for Job Type");
    }

    // Risk assessment
    if (reliability.tier === "Risk") riskFlags.push("High Risk Assignment");
    if (scorecard.issueRate > 0.3) riskFlags.push("High Issue Rate");
    if (scorecard.avgCreditPerJob > 200) riskFlags.push("High Credit Impact");
    if (scorecard.acceptanceRate < 50 && scorecard.totalOpps > 2) riskFlags.push("Low Acceptance Rate");

    // Composite dispatch score
    let score = reliability.score; // base from reliability (0-100)
    
    // Trade match bonus
    if (tradeMatch) score += 10;
    
    // Region match bonus
    if (regionMatch) score += 8;

    // Availability
    if (!availabilityOk) score -= 30;
    if (contractor.availabilityStatus === "Limited") score -= 10;

    // Experience bonus
    if (jobTypeExperience >= 5) score += 10;
    else if (jobTypeExperience >= 2) score += 5;

    // Acceptance rate factor
    if (scorecard.acceptanceRate >= 80) score += 5;
    else if (scorecard.acceptanceRate < 40 && scorecard.totalOpps > 2) score -= 10;

    score = Math.max(0, Math.min(100, score));

    return {
      contractor,
      score,
      reliabilityTier: reliability.tier,
      reliabilityScore: reliability.score,
      riskFlags,
      tradeMatch,
      regionMatch,
      availabilityOk,
      jobTypeExperience,
      rank: 0,
    };
  });

  // Sort by score descending
  recommendations.sort((a, b) => b.score - a.score);
  recommendations.forEach((r, i) => r.rank = i + 1);

  return {
    jobReady,
    readinessStatus: readiness.status,
    recommendations: recommendations.slice(0, 10), // top 10
    topPick: recommendations[0] || null,
    // `blocked` is reserved for terminal states (e.g. job already completed).
    // Readiness gaps surface via `gated` + `gateBlockers` so assignment stays available.
    blocked: false,
    blockReason: undefined,
    gated,
    gateReason,
    gateBlockers,
  };
}

export function getPerformanceLabel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "Top Performer", color: "text-success" };
  if (score >= 55) return { label: "Average", color: "text-warning" };
  return { label: "High Risk", color: "text-destructive" };
}

export function getPerformanceBadgeClasses(score: number): string {
  if (score >= 80) return "bg-success/10 text-success border-success/20";
  if (score >= 55) return "bg-warning/10 text-warning border-warning/20";
  return "bg-destructive/10 text-destructive border-destructive/20";
}
